"use client";

import { useMapStore } from "@/lib/stores/mapStore";
import { useHotelInfoStore } from "@/lib/stores/hotelInfoStore";
import { HotelResponse } from "@/lib/Types/HotelInfoResponse/HotelInfoResponse";
import { useEffect } from "react";
import { useTheme } from "styled-components";
import { Text, Button } from "@lmn/design-system";
import { StarsWithTooltip } from "@/components/UI/Stars/Stars";
import AddressMapLink from "@/components/UI/AddressMapLink/AddressMapLink";
import RecommendationRate from "@/components/UI/RecommendationRate/RecommendationRate";
import ReviewsCount from "@/components/UI/ReviewsCount/ReviewsCount";
import IconArrowRight from "@lmn/lastminute-react-icons/icons-converted/lastminute-icons-solid/IconArrowRight.js";
import IconPin from "@lmn/lastminute-react-icons/icons-converted/lastminute-icons-solid/IconPin.js";
import { scrollToReviewsTab, openReviewsDialog } from "../Helpers/scrollToReviewsTab";
import {
  getColorByPercentage,
  getLabelByPercentage,
} from "@/lib/Helpers/ratings";
import {
  Container,
  DesktopFlexRow,
  FlexCol,
  InfoContainer,
  MobileRows,
  MobileRow,
  MobileRowWithBorderBottom,
  MobileTitleRow,
  RatingBadge,
  MobileAddressContent,
  MobileAddressText,
  ReviewsRowContent,
} from "./HotelTitle.styles";
import { sendKibanaLog } from "@/app/api/sendKibanaLog";
import { useReactiveSearchParams } from "@/lib/Hooks/useReactiveSearchParams";
import { EnvConfig } from "@/lib/Helpers/getServerConfig";

export default function HotelTitleClient({
  hotelTitleData,
  mapBoxApiKey,
  envConfig,
}: {
  hotelTitleData: HotelResponse;
  mapBoxApiKey: string;
  envConfig: EnvConfig;
}) {
  const { setShowMap, setMapData } = useMapStore();
  const setHotelInfo = useHotelInfoStore((state) => state.setHotelInfo);
  const theme = useTheme();
  const searchParams = useReactiveSearchParams();
  const searchId = searchParams.get("searchId") ?? "";


  // Update the store when the hotel data is available
  useEffect(() => {
    // Normalize GeoVerbose (latitude/longitude) to Geo (lat/long) for Map component
    const normalizedGeo = hotelTitleData.hotelData.geo
      ? {
          accuracy: hotelTitleData.hotelData.geo.accuracy,
          lat: hotelTitleData.hotelData.geo.latitude,
          long: hotelTitleData.hotelData.geo.longitude,
        }
      : null;

    setHotelInfo(
      hotelTitleData.hotelData.hotelName,
      hotelTitleData.hotelData.id.toString()
    );
    setMapData(normalizedGeo ? [normalizedGeo] : [], mapBoxApiKey);
  }, [hotelTitleData, setHotelInfo, setMapData, mapBoxApiKey]);

  const { recommendationRate, reviewsCount, ratingAverage } =
    hotelTitleData.hotelData;
  const ratingValue = ratingAverage ?? 0;
  const ratingLabel = getLabelByPercentage(recommendationRate);
  const ratingColor = getColorByPercentage(recommendationRate, theme);

  const logInteraction = (message: string) => {
    sendKibanaLog({ envConfig, message, category: "interaction", searchId });
  };

  return (
    <Container>
      <MobileTitleRow>
        <Text variant="Title1">
          {hotelTitleData.hotelData.hotelName}
        </Text>
        <StarsWithTooltip
          amount={hotelTitleData.hotelData.stars}
          size="medium"
        />
      </MobileTitleRow>

      <Text variant="Title1" margin={{ bottom: "space100" }} className="hidden-on-sm">
        {hotelTitleData.hotelData.hotelName}
      </Text>

      {/* Desktop layout */}
      <DesktopFlexRow>
        <FlexCol>
          <AddressMapLink
            address={hotelTitleData.hotelData.address}
            city={hotelTitleData.hotelData.city}
            region={hotelTitleData.hotelData.region}
            country={hotelTitleData.hotelData.country}
            onClick={() => {
              setShowMap(true);
              logInteraction("Map Opened from Hotel Title");
            }}
          />
          <InfoContainer>
            <StarsWithTooltip
              amount={hotelTitleData.hotelData.stars}
              size="medium"
            />
            ·
            <RecommendationRate rate={recommendationRate} />
            ·
            <ReviewsCount
              count={reviewsCount}
              isGood={recommendationRate > 65}
              onClick={() => {
                scrollToReviewsTab();
                logInteraction("Reviews Link Clicked from Hotel Title");
              }}
            />
          </InfoContainer>
        </FlexCol>
        <Button
          variant="secondary"
          onClick={() => {
            document
              .getElementById("all-offers")
              ?.scrollIntoView({ behavior: "smooth" });
            logInteraction("Angebote anzeigen Button Clicked");
          }}
        >
          Angebote anzeigen
        </Button>
      </DesktopFlexRow>

      {/* Mobile layout: stacked rows */}
      <MobileRows>
        <MobileRow
          onClick={() => {
            setShowMap(true);
            logInteraction("Map Opened from Hotel Title");
          }}
          role="button"
          tabIndex={0}
        >
          <MobileAddressContent>
            <IconPin size={20} />
            <MobileAddressText forceTag="span" variant="BodySmall" color="textSecondary">
              {hotelTitleData.hotelData.address.street && hotelTitleData.hotelData.address.streetNumber && (
                <>{hotelTitleData.hotelData.address.street}, {hotelTitleData.hotelData.address.streetNumber},{" "}</>
              )}
              {hotelTitleData.hotelData.city.name}, {hotelTitleData.hotelData.region.name}, {hotelTitleData.hotelData.country.name}
            </MobileAddressText>

          </MobileAddressContent>
          <IconArrowRight size={16} />
        </MobileRow>

        <MobileRowWithBorderBottom
          onClick={() => {
            openReviewsDialog();
            logInteraction("Reviews Link Clicked from Hotel Title");
          }}
          role="button"
          tabIndex={0}
        >
          <ReviewsRowContent>
            <RatingBadge $bgColor={ratingColor}>
              {ratingValue.toFixed(1)}
            </RatingBadge>
            <Text variant="BodySmall" weight="medium">{ratingLabel}</Text>
            <Text variant="BodySmall" color="textSecondary">{reviewsCount} Bewertungen</Text>
          </ReviewsRowContent>
          <IconArrowRight size={16} />
        </MobileRowWithBorderBottom>
      </MobileRows>
    </Container>
  );
}
